import React, {useEffect, useRef, useState} from 'react';
import {Checkbox, Col, Form, Input, message, Row} from 'antd';
import ProForm, {ProFormInstance} from '@ant-design/pro-form';
import {WdiCardTitle, wdiCore, WdiModalAppResult} from '@/components/WdiControls';
import {FileUploadUtility, WdiFileUpload} from '@/components/WdiControls/WdiFileUpload';
import {PageType} from '@/services/basic/pagetype';
import {Enum} from '@/enums/system';
import {ResponseStatus} from '@/utils/request';
import {FormPageProps} from '@/components/WdiBus/FormPage';
import DataStore from '@/components/GridWidget/DataStore';
import {ApplyFooter, ApplyFooterType} from '@/components/WdiBus/ApplyFooter';
import {getPageResult, initApplyPageContext, ViewEntityProps} from '../../components/data';
import {FormSp} from '@/services/sales/sp';
import {WdiPanel} from '@/components/WdiControls/WdiPanel';
import FormSaInfosApp from '@/pages/sales/sa/components/FormSaInfos';
import FormSsInfosApp from '@/pages/sales/ss/components/FormSsInfos';
import {WarningInfoAppProps} from '@/components/WdiBus/PanelForWarning';
import SPWarningInfoApp from '@/pages/sales/sp/components/WarningInformation';
import {WdiSelect} from '@/components/WdiControls/WdiSelect';
import {VatType} from '@/services/mdm/vattype';
import {GeneralPaymentDetailsForApplyPanel} from '@/pages/sales/sp/components/detail';
import {WdiEditor} from "@/components/WdiControls/WdiEditor";
import {ShipTo} from "@/services/mdm/shipto";

const BillDeductPaymentApplyApp: React.FC<FormPageProps> = (props) => {
  const formRef = useRef<ProFormInstance>();
  const [entity, setEntity] = useState<ViewEntityProps>();
  const [pageType, setPageType] = useState<any>();
  const [warningSettings, setWarningSettings] = useState<WarningInfoAppProps>({visible: false});
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [vatTypes, setVatTypes] = useState<API.VatType[]>([]);
  const [vatTypeSelect, setVatTypeSelect] = useState<API.VatType>();
  const [editorRef, setEditorRef] = useState<any>()


  const onDetailsChange = (data: any) => {
    setDataSource(data);
  };

  const onVatTypeChange = (vatType?: API.VatType) => {
    setVatTypeSelect(vatType);
    if (dataSource && dataSource.length > 0 && vatType) {
      const newDataSource: any[] = [];
      dataSource.forEach(item => {
        if (!wdiCore.Object.IsNullOrUndefined(item.amountInVat)) {
          item.amountExVat = wdiCore.Number.ToNumber(item.amountInVat / (1 + vatType.vatTypeValue), 2);
          item.tax = wdiCore.Number.ToNumber(item.amountInVat - item.amountExVat, 2);
          newDataSource.push(Object.assign({}, item));
        } else {
          newDataSource.push(item);
        }
      });
      setDataSource(newDataSource);
    }
  }

  useEffect(() => {
    (async () => {
      const params = props.data;
      if (params) {
        const result = await initApplyPageContext(params);
        if (result.code !== ResponseStatus.SUCCESS) {
          message.error(result.message);
          return;
        }
        const viewEntity: ViewEntityProps = result.data;

        if (viewEntity.formSp) {
          formRef.current?.setFieldsValue({
            vatTypeId: viewEntity.vatTypeId,
            reimburseMaterials: viewEntity.formSp?.reimburseMaterial?.split(','),
            remark: viewEntity.formSp.remark,
            fileInfo: FileUploadUtility.parse(viewEntity.formSp.fileName, viewEntity.formSp.filePath)
          });
        }
        if (viewEntity.paymentMethod) {
          formRef.current?.setFieldsValue({
            paymentMethodName: viewEntity.paymentMethod.name,
          });
          const vatTypeArr = [];
          if (viewEntity.paymentMethod.value == Enum.PaymentMethod.BILL_DEDUCT
            || viewEntity.paymentMethod.value == Enum.PaymentMethod.FEE_POOL) {
            const response = await VatType.getVAT13();
            vatTypeArr.push(response.data);
          } else if (viewEntity.paymentMethod.value == Enum.PaymentMethod.BUY_GIVE_POOL
            || viewEntity.paymentMethod.value == Enum.PaymentMethod.FG_POOL) {
            const response = await VatType.getVAT0();
            vatTypeArr.push(response.data);
          } else {
            const response = await VatType.search({isActive: true});
            vatTypeArr.push(...response.data);
          }
          setVatTypes(vatTypeArr);
          if (vatTypeArr.length == 1) {
            onVatTypeChange(vatTypeArr[0]);
            formRef.current?.setFieldsValue({
              vatTypeId: vatTypeArr[0].vatTypeId,
            });
          }
          if (viewEntity.vatTypeId) {
            const vatType = vatTypeArr.find(item => item.vatTypeId == viewEntity.vatTypeId);
            onVatTypeChange(vatType);
            formRef.current?.setFieldsValue({
              vatTypeId: viewEntity.vatTypeId,
            });
          }
        }
        if (viewEntity.pageTypeId) {
          PageType.get(viewEntity.pageTypeId).then((response) => {
            setPageType(response.data);
          });
        }
        if (viewEntity?.paymentMethod?.value == Enum.PaymentMethod.BILL_DEDUCT && viewEntity.formSpDetails) {
          const newDataSource: any[] = [...viewEntity.formSpDetails];
          const soldToCodes = new Set<string>(newDataSource.map((item: any) => (item.soldToCode)));
          const shipMap: Map<string, API.ShipTo | undefined> = new Map();
          for (const soldToCode of soldToCodes) {
            const shipToRes = await ShipTo.ajaxSearch({top: 2, isActive: true, soldToCode: soldToCode});
            shipMap.set(soldToCode, shipToRes.data.length == 1 ? shipToRes.data[0] : undefined);
          }
          for (const record of newDataSource.filter(item => !item.shipToCode)) {
            record.shipToCode = shipMap.get(record.soldToCode)?.shipToCode;
            record.shipToName = shipMap.get(record.soldToCode)?.shipToName;
          }
          setDataSource(DataStore.init(newDataSource));
        } else {
          setDataSource(DataStore.init(viewEntity.formSpDetails));
        }
        setEntity(viewEntity);
      }
    })();
  }, [props.data]);

  useEffect(() => {
    if (props.setPageTitle && pageType && entity) {
      let title = "活动核销申请";
      if (entity.formSp) {
        title += '【编辑】'
      }
      props.setPageTitle(title);
    }
  }, [props.setPageTitle, pageType, entity]);

  return (
    <>
      <SPWarningInfoApp {...warningSettings} />
      <ProForm
        formRef={formRef}
        submitter={{
          render: () => {
            return (
              <ApplyFooter
                setLoading={props.setLoading}
                hiddenButton={[ApplyFooterType.Validate]}
                viewEntity={entity}
                submitBackUrl="/sales/sp/mypayment"
                doSubmit={async (type: number, forceSubmit: boolean) => {
                  if (!entity || !pageType) {
                    return;
                  }
                  const remark = await editorRef?.current?.getHtml?.();
                  const values = await formRef.current?.validateFieldsReturnFormatValue?.();
                  if (values && values.errorFields && values.errorFields.length) {
                    return;
                  }
                  const statusId =
                    type === ApplyFooterType.Draft
                      ? Enum.FormStatus.DRAFT
                      : Enum.FormStatus.AWAITING;
                  values.remark = remark;
                  const req = getPageResult(values, entity, pageType, statusId, dataSource);
                  if (req.code !== ResponseStatus.SUCCESS) {
                    message.error(req.message);
                    return;
                  }
                  props?.setLoading?.(true);
                  return await FormSp.Buttons.apply({
                    forceSubmit: forceSubmit,
                    ...req.data,
                  });
                }}
                del={async () => {
                  if (!entity || !entity.formId) {
                    return;
                  }
                  return await FormSp.Buttons.del(entity.formId);
                }}
                showWarning={(result: WdiModalAppResult, submitCallback: any) => {
                  setWarningSettings({
                    visible: true,
                    title: '警示信息',
                    wdiPanelList: result.data,
                    onClose: () => {
                      setWarningSettings({visible: false});
                    },
                    onSave: async () => {
                      return await submitCallback();
                    },
                  });
                }}
              />
            );
          },
        }}
      >
        {entity?.formSaId && (
          <FormSaInfosApp formRef={formRef} defaultCollapsed={true} formSaId={entity?.formSaId}/>
        )}
        {entity?.formSsId && (
          <FormSsInfosApp formRef={formRef} defaultCollapsed={true} formSsId={entity.formSsId}/>
        )}
        <WdiPanel title={<WdiCardTitle iconType={Enum.IconType.FORM_SP} title="活动核销单信息"/>}>
          <Row gutter={[16, 0]}>
            <Col className="gutter-row" span={4}>
              <Form.Item
                name="vatTypeId"
                label="可抵扣税率类型(普票为0)"
                rules={[{required: true, message: '可抵扣税率类型不能为空'}]}
              >
                <WdiSelect
                  {...{
                    allowClear: false,
                    placeholder: '请选择可抵扣税率类型',
                    disabled:
                      entity?.paymentMethodId == Enum.PaymentMethod.BILL_DEDUCT ||
                      entity?.paymentMethodId == Enum.PaymentMethod.FEE_POOL ||
                      entity?.paymentMethodId == Enum.PaymentMethod.FG_POOL ||
                      entity?.paymentMethodId == Enum.PaymentMethod.BUY_GIVE_POOL,
                    onChange: (value) => {
                      const vatType = vatTypes.find((item) => item.vatTypeId == value);
                      onVatTypeChange(vatType);
                    },
                    options: vatTypes?.map((item) => ({
                      value: item.vatTypeId,
                      label: item.vatTypeName,
                    })),
                  }}
                />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item name="paymentMethodName" label="支付方式">
                <Input readOnly/>
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={16}>
              <Form.Item name="reimburseMaterials" label="核销支持文件">
                <Checkbox.Group
                  options={(entity?.reimburseMaterials || []).map((item) => ({
                    label: item.name,
                    value: String(item.value),
                  }))}
                ></Checkbox.Group>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 24]}>
            <Col className="gutter-row" span={16}>
              <Form.Item name="remark" label="备注">
                <WdiEditor setRef={setEditorRef}/>
              </Form.Item>
            </Col>
            <Col className="gutter-ferow" span={8}>
              <Form.Item
                name="fileInfo"
                label="附件"
                valuePropName="fileList"
                getValueFromEvent={(e: any) => {
                  return e.fileList || [];
                }}
              >
                <WdiFileUpload leftTopInfo="请选择文件并上传(单个文件不能超500M)"/>
              </Form.Item>
            </Col>
          </Row>
        </WdiPanel>
        <GeneralPaymentDetailsForApplyPanel
          data={{
            allowViewSfa: entity?.allowViewSfa,
            formSp: {
              pageTypeId: entity?.pageTypeId,
              formSaId: entity?.formSaId,
              paymentMethodId: entity?.paymentMethodId,
              vatTypeId: vatTypeSelect?.vatTypeId,
            },
            vatType: vatTypeSelect,
            ecFieldConfigs: entity?.ecFieldConfigs,
          }}
          dataSource={dataSource}
          onChange={onDetailsChange}
        />
      </ProForm>
    </>
  );
};
export default BillDeductPaymentApplyApp;
